<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { createVoucherColumns } from '@/tables/columns/vouchers';
    import { computed } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import type { SharedData, BreadcrumbItem, NavItem, Voucher } from '@/types';
    import MissingVouchers from '@/components/vouchers/MissingVouchers.vue';
    import VoucherBulkActions from '@/components/vouchers/VoucherBulkActions.vue';
    import VoucherClosePeriod from '@/components/vouchers/VoucherClosePeriod.vue';
    import VoucherApproveAll from '@/components/vouchers/VoucherApproveAll.vue';
    import VoucherReleaseAll from '@/components/vouchers/VoucherReleaseAll.vue';
    import VoucherStatementUpload from '@/components/vouchers/VoucherStatementUpload.vue';
    import VoucherClearList from '@/components/vouchers/VoucherClearList.vue';
    import VoucherAdministrate from '@/components/vouchers/VoucherAdministrate.vue';
    import QueryKey from '@/enums/QueryKey';

    const { hasPermission, hasAnyPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    
    const { data: economicDimensions } = useQuery({
        queryKey: ['economic-dimensions', selectedCompanyId],
        queryFn: async () => {
            if (!selectedCompanyId.value) return [];
            const { data } = await axios.get(route('v2.voucher.dimensions.get', selectedCompanyId.value));
            return data || [];
        },
        enabled: computed(() => !!selectedCompanyId.value),
        placeholderData: [],
        retry: 1,
        refetchOnMount: true,
    });
    
    const columns = computed(() => createVoucherColumns(economicDimensions.value || []));

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'vouchers.page_header',
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'vouchers.current_vouchers.header',
            href: route('v2.vouchers.index'),
            permissionLevel: Permission.VOUCHERS_VIEW || Permission.VOUCHERS_OWN_VIEW,
        },
        {
            title: 'vouchers.earlier_releases.header',
            href: route('v2.vouchers.earlier-releases.index'),
            permissionLevel: Permission.VOUCHERS_EARLIER_RELEASES_VIEW,
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.VOUCHERS, selectedCompanyId],
        queryFn: getVouchers,
    });

    async function getVouchers() {
        let vouchersRoute = '';
        if (
            hasPermission(Permission.VOUCHERS_OWN_VIEW) &&
            !hasPermission(Permission.VOUCHERS_VIEW)
        ) {
            vouchersRoute = 'v2.vouchers.own.table';
        } else {
            vouchersRoute = 'v2.vouchers.table';
        }

        const { data } = await axios.get(route(vouchersRoute, selectedCompanyId.value));

        return data.data;
    }

    const missingVouchers = computed(() => {
        if (!data.value) return 0;
        return data.value.filter((voucher: Voucher) => voucher.uploaded === 0).length;
    });

    const totalVouchers = computed(() => {
        return data.value?.length || 0;
    });

    const hasMissingVouchers = computed(() => {
        return data.value && missingVouchers.value > 0;
    });
</script>

<template>
    <Head :title="$t('vouchers.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.VOUCHERS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :hideControls="true"
                :manualPagination="true"
                :default-sorting="[
                    {
                        id: 'Date',
                        desc: true,
                    },
                ]"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                    <MissingVouchers
                        :missing-vouchers="missingVouchers"
                        :total-vouchers="totalVouchers"
                    />
                </template>
                <template #table-top="{ table }">
                    <div class="items center flex flex-wrap gap-4">
                        <VoucherClosePeriod
                            v-if="
                                hasPermission(Permission.VOUCHERS_ADMIN_UPDATE) &&
                                !isPending &&
                                !hasMissingVouchers
                            "
                        />
                        <VoucherApproveAll v-if="hasPermission(Permission.VOUCHERS_UPDATE)" />
                        <VoucherReleaseAll v-if="hasPermission(Permission.VOUCHERS_ADMIN_CREATE)" />
                        <VoucherStatementUpload
                            v-if="hasPermission(Permission.VOUCHERS_ADMIN_UPDATE)"
                        />
                        <VoucherClearList v-if="hasPermission(Permission.VOUCHERS_ADMIN_DELETE)" />

                        <VoucherAdministrate
                            v-if="hasPermission(Permission.VOUCHERS_ADMIN_UPDATE)"
                        />

                        <VoucherBulkActions
                            v-if="
                                hasAnyPermission([
                                    Permission.VOUCHERS_ADMIN_UPDATE,
                                    Permission.VOUCHERS_ADMIN_DELETE,
                                ])
                            "
                            :table="table"
                        />
                    </div>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
